import * as Yup from 'yup';
import { validation } from 'src/utils/validation';
import { RubberSleeveSize } from 'src/shared/enums/rubberSleeveSize.enum';
import { RubberGloveSizeEnum } from 'src/shared/enums/rubberGlovesSize.enum';
import { WorkVestSizeEnum } from 'src/shared/enums/workWestSize.enum';
import { WorkGloveSizeEnum } from 'src/shared/enums/workGlovesSize.enum';
import { EmergencyContactRelationship } from 'src/shared/enums/emergencyContactRelationship.enum';
import { EmploymentTypeEnum } from 'src/shared/enums/employmentType.enum';
import { RoleSlug } from 'src/shared/enums/roleSlug.enum';
import { toCamelCase } from 'src/utils/utils';
import config from 'src/config';
import { union_classifications as unionClassifications, unions } from 'src/localizations/unionClassifications.en';

function normalizePhone(input: string): string | null {
  const digits = input.replace(/\D/g, '');

  if (digits.length === 10) {
    return `+1${digits}`;
  }

  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }

  return null;
}

function getUnionClassifications() {
  const defaultUnionClassifications: Record<string, string[]> = {};
  for (const [union, classifications] of Object.entries(unionClassifications)) {
    defaultUnionClassifications[union] = Object.keys(classifications);
  }

  return config.options.UNION_CLASSIFICATIONS?.length ? config.options.UNION_CLASSIFICATIONS : defaultUnionClassifications;
}

const UNIONS = config.options.UNIONS?.length ? config.options.UNIONS : Object.keys(unions);
const UNION_CLASSIFICATIONS = getUnionClassifications();

const ENUM_FIELDS: Record<string, Record<string, any>> = {
  employmentType: EmploymentTypeEnum,
  emergencyRelationship: EmergencyContactRelationship,
};

const dateOfBirthSchema = Yup.object({
  day: Yup.number().integer().min(1).max(31).notRequired(),
  month: Yup.number().integer().min(1).max(12).notRequired(),
  year: Yup.number().integer().max(new Date().getFullYear()).notRequired(),
});

const baseUserSchema = Yup.object({
  email: Yup.string().matches(validation.email, 'Invalid email').required('Email Cannot Be Empty'),
  role: Yup.string().oneOf(Object.values(RoleSlug)).required('Role Cannot Be Empty'),
  phone: Yup.string().max(13).matches(validation.phonePlusOne, 'Invalid phone').notRequired(),
  firstName: Yup.string().min(3).max(64).required('First Name Cannot Be Empty'),
  lastName: Yup.string().min(3).max(64).required('Last Name Cannot Be Empty'),
  middleName: Yup.string().min(3).max(64).notRequired(),
  preferredName: Yup.string().min(3).max(64).notRequired(),
  ssn: Yup.string().matches(validation.ssn, 'Invalid SSN').notRequired(),
  rubberSleeve: Yup.string().oneOf(Object.keys(RubberSleeveSize)).notRequired(),
  rubberGlove: Yup.string().oneOf(Object.keys(RubberGloveSizeEnum)).notRequired(),
  workVest: Yup.string().oneOf(Object.keys(WorkVestSizeEnum)).notRequired(),
  workGlove: Yup.string().oneOf(Object.keys(WorkGloveSizeEnum)).notRequired(),
  zipcode: Yup.string().matches(validation.zipCode, 'Invalid ZIP').notRequired(),
  emergencyEmail: Yup.string().email().max(128).notRequired(),
  emergencyPhone: Yup.string().max(13).matches(validation.phonePlusOne, 'Invalid emergency phone').notRequired(),
  emergencyFirstName: Yup.string().min(3).max(64).notRequired(),
  emergencyLastName: Yup.string().min(3).max(64).notRequired(),
  emergencyRelationship: Yup.string().oneOf(Object.values(EmergencyContactRelationship)).notRequired(),
  employmentType: Yup.number()
    .oneOf(
      Object.values(EmploymentTypeEnum).filter((v) => typeof v === 'number') as number[],
      'Invalid employment type. Should be UNION or NON_UNION',
    )
    .notRequired(),
  country: Yup.string().max(64).notRequired(),
  city: Yup.string().max(64).notRequired(),
  region: Yup.string().notRequired(),
  state: Yup.string().max(64).notRequired(),
  streetAddress: Yup.string().max(64).notRequired(),
  streetAddress2: Yup.string().max(64).notRequired(),
  dateOfBirth: Yup.object()
    .nullable()
    .notRequired()
    .test('custom-error-wrapper', 'Date of birth must be in MM/DD/YYYY format', (value) => {
      if (!value) return true;
      try {
        dateOfBirthSchema.validateSync(value);
        return true;
      } catch {
        return false;
      }
    }),
  driverLicenseExpiration: Yup.number()
    .transform((_, originalValue) => {
      if (!originalValue) return undefined;

      const [monthStr, dayStr, yearStr] = originalValue.split('/');
      const month = Number(monthStr);
      const day = Number(dayStr);
      const year = Number(yearStr);

      if (
        !/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(originalValue) ||
        isNaN(month) ||
        isNaN(day) ||
        isNaN(year) ||
        month < 1 ||
        month > 12 ||
        day < 1 ||
        day > 31
      ) {
        return NaN;
      }

      const isoStr = `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      const date = new Date(isoStr);

      if (isNaN(date.getTime()) || date.getMonth() + 1 !== month || date.getDate() !== day) {
        return NaN;
      }

      return date.getTime();
    })
    .typeError('Driver license date must be in MM/DD/YYYY format.')
    .notRequired(),
  unionTicket: Yup.string().notRequired(),
  localUnion: Yup.string().oneOf(UNIONS).notRequired(),
  unionClassification: Yup.string()
    .max(64)
    .nullable()
    .notRequired()
    .when('localUnion', ([localUnion], schema) => {
      const allowed = UNION_CLASSIFICATIONS[localUnion ?? ''];

      if (localUnion && Array.isArray(allowed) && allowed.length > 0) {
        return schema.oneOf(allowed, 'Invalid union classification for selected union');
      }

      return schema.test(
        'should-be-empty-if-no-union',
        'Local union should be selected before choosing a union classification.',
        (val) => val == null || val.trim() === '',
      );
    }),
});

function normalizeKey(key: string): string {
  return toCamelCase(
    key
      .replace(/\s+/g, '_')
      .replace(/\*/g, '')
      .replace(/\([^)]*\)/g, '')
      .replace(/_+$/, '')
      .trim(),
  );
}

export type InvalidUser = {
  id: number;
  rowNumber: number;
  errors: Record<string, string>;
  [key: string]: any;
};

export interface CsvValidationResult {
  validUsers: any[];
  invalidUsers: InvalidUser[];
  rowErrors: Record<number, string[]>;
}

export function validateUsersCsvRows(rows: any[], headers: string[]): CsvValidationResult {
  const validUsers: any[] = [];
  const invalidUsers: InvalidUser[] = [];
  const rowErrors: Record<number, string[]> = {};
  const headerMap: Record<string, string> = {};

  if (headers && Array.isArray(headers)) {
    headers.forEach((header: string) => {
      headerMap[header] = normalizeKey(header);
    });
  }

  rows.forEach((row: any, index: number) => {
    if (!row || Object.values(row).every((v) => v === undefined || String(v).trim() === '')) {
      return;
    }

    const normalizedRow: Record<string, any> = {};
    Object.keys(row).forEach((k) => {
      const normKey = headerMap[k] || normalizeKey(k);
      const value = row[k];
      if (value !== undefined && String(value).trim() !== '') {
        normalizedRow[normKey] = value;
      }
    });

    Object.entries(ENUM_FIELDS).forEach(([field, enumObj]) => {
      if (normalizedRow[field] && enumObj[normalizedRow[field]] !== undefined) {
        normalizedRow[field] = enumObj[normalizedRow[field]];
      }
    });

    if (normalizedRow.dateOfBirth && typeof normalizedRow.dateOfBirth === 'string') {
      const [month, day, year] = normalizedRow.dateOfBirth.split('/').map(Number);
      if (year && month && day) {
        normalizedRow.dateOfBirth = { year, month, day };
      }
    }

    if (normalizedRow.phone) {
      const normalized = normalizePhone(normalizedRow.phone);
      if (normalized) {
        normalizedRow.phone = normalized;
      }
    }

    if (normalizedRow.emergencyPhone) {
      const normalized = normalizePhone(normalizedRow.emergencyPhone);
      if (normalized) {
        normalizedRow.emergencyPhone = normalized;
      }
    }

    const errs: string[] = [];

    try {
      const validatedRow = baseUserSchema.validateSync(normalizedRow, { abortEarly: false });
      validUsers.push(validatedRow);
    } catch (e) {
      if (e instanceof Yup.ValidationError) {
        errs.push(...e.errors);
      } else {
        errs.push('Unknown validation error');
      }

      rowErrors[index + 2] = errs;

      const normalizedInvalidRow = Object.entries(row).reduce(
        (acc, [key, value]) => {
          acc[normalizeKey(key)] = value;
          return acc;
        },
        {} as Record<string, any>,
      );

      const errorMap = (e as Yup.ValidationError).inner.reduce(
        (acc, error) => {
          if (error.path) {
            acc[error.path] = error.message;
          }
          return acc;
        },
        {} as Record<string, string>,
      );

      invalidUsers.push({
        id: index,
        rowNumber: index + 2,
        ...normalizedInvalidRow,
        errors: errorMap,
      });
    }
  });

  return { validUsers, invalidUsers, rowErrors };
}
